// store — the Studio Zustand store. Holds the working RuleSet + master catalog and an EngineClient
// (Web Worker in the browser, local dispatcher in tests). Every rule edit bumps `revision`, which is
// the "save → reflect" signal: panels read `revision` and re-query the engine, so the open
// ScoreTableInspector / ExampleHand / PlaySandbox re-evaluate against the new rules without a reload
// (spec §6.2: re-instantiate the engine view on every edit, <~100ms, sandbox state preserved).
import { createStore } from 'zustand/vanilla';
import type { RuleSet, YakuDefMeta, WinContext, Seat, TileId } from '@mj/protocol';
import { YAKU_CATALOG } from '@mj/engine';
import { studioDefaultRuleSet } from './defaults';
import { createDefaultEngineClient, type EngineClient } from './engineClient';

export interface StudioState {
  ruleSet: RuleSet;
  catalog: YakuDefMeta[];
  engine: EngineClient;
  /** Bumped on every rule edit / config load — the save→reflect re-evaluation trigger. */
  revision: number;
  /** Set after a successful save bridge POST; used to confirm "derived, reconstructable" state. */
  lastSavedAt: number | null;

  // ---- actions ----
  setRuleSet(next: RuleSet): void;
  patchScoring(patch: Partial<RuleSet['scoring']>): void;
  patchFlags(patch: Partial<RuleSet['flags']>): void;
  patchPoints(patch: Partial<RuleSet['points']>): void;
  patchTiles(patch: Partial<RuleSet['tiles']>): void;
  toggleYaku(id: string, enabled: boolean): void;
  setHanOverride(id: string, han: number | undefined): void;
  /** Append (or replace by id) a custom yaku def in the WORKING catalog, enable it, bump revision. */
  addYaku(def: YakuDefMeta): void;
  /** Remove a Studio-authored (custom) yaku + its enabledYaku room. No-op for built-ins. */
  removeCustomYaku(id: string): void;
  markSaved(): void;
  /** Replace the working RuleSet and/or catalog from persisted config on boot (save→reload restore). */
  hydrate(next: { ruleSet?: RuleSet; catalog?: YakuDefMeta[] }): void;
}

/**
 * Marker put on the `source` field of Studio-authored yaku so the remove UI can tell custom
 * (user-added) entries apart from the built-in catalog. Built-in entries either have no `source`
 * or carry a provenance note (e.g. 'ja.wikipedia local yaku'); only this exact tag means "custom".
 */
export const CUSTOM_YAKU_SOURCE = 'studio:custom';

/** True for a Studio-authored (removable) yaku — distinct from built-in catalog entries. */
export function isCustomYaku(def: YakuDefMeta): boolean {
  return def.source === CUSTOM_YAKU_SOURCE;
}

export function createStudioStore(engine: EngineClient = createDefaultEngineClient()) {
  return createStore<StudioState>((set, get) => ({
    ruleSet: studioDefaultRuleSet(4),
    catalog: YAKU_CATALOG,
    engine,
    revision: 0,
    lastSavedAt: null,

    setRuleSet(next) {
      set({ ruleSet: next, revision: get().revision + 1 });
    },
    patchScoring(patch) {
      const rs = get().ruleSet;
      set({ ruleSet: { ...rs, scoring: { ...rs.scoring, ...patch } }, revision: get().revision + 1 });
    },
    patchFlags(patch) {
      const rs = get().ruleSet;
      set({ ruleSet: { ...rs, flags: { ...rs.flags, ...patch } }, revision: get().revision + 1 });
    },
    patchPoints(patch) {
      const rs = get().ruleSet;
      set({ ruleSet: { ...rs, points: { ...rs.points, ...patch } }, revision: get().revision + 1 });
    },
    patchTiles(patch) {
      const rs = get().ruleSet;
      set({ ruleSet: { ...rs, tiles: { ...rs.tiles, ...patch } }, revision: get().revision + 1 });
    },
    toggleYaku(id, enabled) {
      const rs = get().ruleSet;
      const prev = rs.enabledYaku[id] ?? { enabled: false };
      set({
        ruleSet: { ...rs, enabledYaku: { ...rs.enabledYaku, [id]: { ...prev, enabled } } },
        revision: get().revision + 1,
      });
    },
    setHanOverride(id, han) {
      const rs = get().ruleSet;
      const prev = rs.enabledYaku[id] ?? { enabled: true };
      const nextEntry: { enabled: boolean; hanOverride?: number } = { enabled: prev.enabled };
      if (han !== undefined) nextEntry.hanOverride = han;
      set({
        ruleSet: { ...rs, enabledYaku: { ...rs.enabledYaku, [id]: nextEntry } },
        revision: get().revision + 1,
      });
    },
    addYaku(def) {
      const rs = get().ruleSet;
      const catalog = get().catalog;
      const i = catalog.findIndex((d) => d.id === def.id);
      // Never mutate the (shared, possibly built-in) catalog array: build a fresh list.
      const nextCatalog = i >= 0 ? catalog.map((d) => (d.id === def.id ? def : d)) : [...catalog, def];
      const prev = rs.enabledYaku[def.id];
      set({
        catalog: nextCatalog,
        ruleSet: { ...rs, enabledYaku: { ...rs.enabledYaku, [def.id]: { ...prev, enabled: true } } },
        revision: get().revision + 1,
      });
    },
    removeCustomYaku(id) {
      const catalog = get().catalog;
      const def = catalog.find((d) => d.id === id);
      // Guard: only Studio-authored entries are removable; built-ins are left untouched.
      if (!def || !isCustomYaku(def)) return;
      const rs = get().ruleSet;
      const nextEnabled = { ...rs.enabledYaku };
      delete nextEnabled[id];
      set({
        catalog: catalog.filter((d) => d.id !== id),
        ruleSet: { ...rs, enabledYaku: nextEnabled },
        revision: get().revision + 1,
      });
    },
    markSaved() {
      set({ lastSavedAt: Date.now() });
    },
    hydrate(next) {
      set((s) => {
        // Catalog: MERGE rather than replace. Built-ins ALWAYS come from the live code catalog
        // (YAKU_CATALOG) so newly added builtin yaku appear even when an older catalog.json is on
        // disk; only the persisted CUSTOM (Studio-authored) entries are carried over.
        let catalog = s.catalog;
        if (next.catalog) {
          const customs = next.catalog.filter(isCustomYaku);
          const customIds = new Set(customs.map((d) => d.id));
          catalog = [...YAKU_CATALOG.filter((d) => !customIds.has(d.id)), ...customs];
        }
        let ruleSet = next.ruleSet ?? s.ruleSet;
        // Seed enabledYaku for any catalog yaku missing from the persisted ruleSet (i.e. builtins
        // added since it was saved) so they are enabled-by-default rather than silently disabled.
        const enabledYaku = { ...ruleSet.enabledYaku };
        let seeded = false;
        for (const def of catalog) {
          if (!(def.id in enabledYaku)) {
            enabledYaku[def.id] = { enabled: true };
            seeded = true;
          }
        }
        if (seeded) ruleSet = { ...ruleSet, enabledYaku };
        return { ruleSet, catalog, revision: s.revision + 1 };
      });
    },
  }));
}

export type StudioStore = ReturnType<typeof createStudioStore>;

// ---------------------------------------------------------------------------
// WinContext builder — assemble a full WinContext from the parts the example-hand / sandbox UIs
// know. Centralised so every panel feeds the scorer a consistent context.
// ---------------------------------------------------------------------------
export interface CtxParts {
  winBy: 'ron' | 'tsumo';
  winner?: Seat;
  from?: Seat | null;
  isDealer?: boolean;
  menzen?: boolean;
  seatWind?: TileId | null;
  roundWind?: TileId;
  firstTurn?: boolean;
  lastTile?: 'none' | 'haitei' | 'houtei';
  afterKan?: boolean;
  riichi?: boolean;
  doubleRiichi?: boolean;
  ippatsu?: boolean;
  openRiichi?: boolean;
  doraCount?: number;
  akaCount?: number;
  uraCount?: number;
  kitaCount?: number;
  flowerCount?: number;
  dealerWinStreak?: number;
}

export function buildWinContext(p: CtxParts): WinContext {
  const winner = (p.winner ?? 0) as Seat;
  return {
    winBy: p.winBy,
    firstTurn: p.firstTurn ?? false,
    lastTile: p.lastTile ?? 'none',
    isDealer: p.isDealer ?? false,
    menzen: p.menzen ?? true,
    afterKan: p.afterKan ?? false,
    winner,
    from: p.from ?? (p.winBy === 'ron' ? ((winner === 0 ? 1 : 0) as Seat) : null),
    seatWind: p.seatWind ?? 'z1',
    roundWind: p.roundWind ?? 'z1',
    riichi: p.riichi ?? false,
    doubleRiichi: p.doubleRiichi ?? false,
    ippatsu: p.ippatsu ?? false,
    openRiichi: p.openRiichi ?? false,
    doraCount: p.doraCount ?? 0,
    akaCount: p.akaCount ?? 0,
    uraCount: p.uraCount ?? 0,
    kitaCount: p.kitaCount ?? 0,
    flowerCount: p.flowerCount ?? 0,
    dealerWinStreak: p.dealerWinStreak ?? 0,
  };
}
